import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { SupervisorLayout } from "@/layouts/SupervisorLayout";
import { BarChart3, Search, CheckCircle2, AlertTriangle, ExternalLink, RefreshCw, Camera, Video } from "lucide-react";

export default function BuildReportsPage() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit, skip };
      if (q.trim()) params.q = q.trim();
      const { data } = await client.get("/admin/build-reports", { params });
      setRows(data?.rows || []);
      setTotal(data?.total || 0);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [q, skip]);

  useEffect(() => { load(); }, [load]);

  const onSearch = (e) => {
    e.preventDefault();
    setSkip(0);
    load();
  };

  const hasMore = skip + rows.length < total;

  return (
    <SupervisorLayout>
      <div className="p-5 md:p-8 max-w-[1600px]">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-white tracking-tight flex items-center gap-3" data-testid="build-reports-heading">
              <BarChart3 size={22} style={{ color: "var(--accent)" }} /> Build Reports
            </h1>
            <p className="mt-1.5 text-white/50 text-sm max-w-2xl">
              Internal archive of completed PC builds. Includes specifications, QC results, and the 5 final photos for each build.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-xs tracking-[0.24em] uppercase text-white/45 font-semibold">
              Total <span className="text-white ml-2 text-sm" data-testid="build-reports-total">{total}</span>
            </div>
            <button
              type="button"
              onClick={() => load()}
              className="inline-flex items-center gap-2 h-9 px-3 rounded-md border text-[11px] font-semibold tracking-wide text-white/70 hover:text-white"
              style={{ borderColor: "rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.02)" }}
              data-testid="build-reports-refresh"
            >
              <RefreshCw size={13} /> REFRESH
            </button>
          </div>
        </div>

        <form onSubmit={onSearch} className="mb-4 flex items-center gap-3 max-w-lg">
          <div
            className="flex-1 flex items-center gap-2 rounded-md border h-10 px-3"
            style={{ borderColor: "rgba(255,255,255,0.12)", background: "#0d0d10" }}
          >
            <Search size={14} className="text-white/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by order number…"
              className="flex-1 bg-transparent text-sm text-white placeholder-white/30 focus:outline-none"
              data-testid="build-reports-search-input"
            />
          </div>
          <button
            type="submit"
            className="h-10 px-4 rounded-md text-[11px] font-semibold tracking-wide border"
            style={{ borderColor: "rgba(0,229,255,0.5)", color: "var(--accent)", background: "rgba(0,229,255,0.05)" }}
            data-testid="build-reports-search-submit"
          >
            SEARCH
          </button>
        </form>

        <div
          className="rounded-xl border overflow-hidden"
          style={{ borderColor: "rgba(255,255,255,0.06)", background: "#0a0a0d" }}
          data-testid="build-reports-list"
        >
          <div
            className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] px-5 py-3 text-[10px] tracking-[0.24em] uppercase text-white/45 font-semibold border-b"
            style={{ borderColor: "rgba(255,255,255,0.06)" }}
          >
            <div>Order</div>
            <div>Completed</div>
            <div>Technician</div>
            <div>Station</div>
            <div>QC</div>
            <div>Media</div>
            <div />
          </div>
          {loading ? (
            <div className="p-6 text-white/40 text-sm" data-testid="build-reports-loading">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-white/50 text-center text-sm" data-testid="build-reports-empty">
              No completed builds found{q ? ` for "${q}"` : ""}.
            </div>
          ) : (
            rows.map((r) => (
              <div
                key={r.order_id}
                className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] items-center px-5 py-3 border-b hover:bg-white/[0.02] transition-colors"
                style={{ borderColor: "rgba(255,255,255,0.04)" }}
                data-testid={`build-reports-row-${r.order_number}`}
              >
                <div className="text-white font-semibold font-mono truncate">#{r.order_number}</div>
                <div className="text-white/70 text-sm">
                  {r.finish_time ? new Date(r.finish_time).toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                </div>
                <div className="text-white/70 text-sm truncate">{r.technician_name || "—"}</div>
                <div className="text-white/70 text-sm">{r.station_id}</div>
                <div>
                  {r.testing_available === false ? (
                    <span className="text-white/40 text-[11px] tracking-[0.18em] uppercase">Legacy</span>
                  ) : r.qc_passed ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold tracking-[0.16em]" style={{ color: "var(--success)" }}>
                      <CheckCircle2 size={12} /> PASSED
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold tracking-[0.16em]" style={{ color: "#ff9800" }}>
                      <AlertTriangle size={12} /> REVIEW
                    </span>
                  )}
                </div>
                <div className="inline-flex items-center gap-3 text-[11px] tracking-[0.14em] font-semibold" data-testid={`build-reports-row-${r.order_number}-media`}>
                  <span className="inline-flex items-center gap-1" style={{ color: r.has_photo ? "var(--success)" : "rgba(255,255,255,0.35)" }}>
                    <Camera size={12} /> PHOTO {r.has_photo ? "✓" : "—"}
                  </span>
                  <span className="inline-flex items-center gap-1" style={{ color: r.has_video ? "var(--success)" : "rgba(255,255,255,0.35)" }}>
                    <Video size={12} /> VIDEO {r.has_video ? "✓" : "—"}
                  </span>
                </div>
                <div className="text-right">
                  <Link
                    to={`/supervisor/build-reports/${r.order_id}`}
                    className="inline-flex items-center gap-2 h-8 px-3 rounded-md text-[11px] font-semibold tracking-[0.14em] border transition-colors"
                    style={{ borderColor: "rgba(0,229,255,0.35)", color: "var(--accent)", background: "rgba(0,229,255,0.04)" }}
                    data-testid={`build-reports-view-${r.order_number}`}
                  >
                    VIEW REPORT <ExternalLink size={12} />
                  </Link>
                </div>
              </div>
            ))
          )}
          {(hasMore || skip > 0) && (
            <div className="flex items-center justify-between px-5 py-3 text-xs text-white/50" data-testid="build-reports-pagination">
              <span>Showing {skip + 1}–{skip + rows.length} of {total}</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={skip === 0}
                  onClick={() => setSkip(Math.max(0, skip - limit))}
                  className="h-8 px-3 rounded-md border text-[11px] font-semibold tracking-wide disabled:opacity-40"
                  style={{ borderColor: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.75)" }}
                  data-testid="build-reports-prev"
                >
                  PREV
                </button>
                <button
                  type="button"
                  disabled={!hasMore}
                  onClick={() => setSkip(skip + limit)}
                  className="h-8 px-3 rounded-md border text-[11px] font-semibold tracking-wide disabled:opacity-40"
                  style={{ borderColor: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.75)" }}
                  data-testid="build-reports-next"
                >
                  NEXT
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </SupervisorLayout>
  );
}
